import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Eye, Edit, Trash2, Bot, CheckCircle, Globe, Code, Wrench, Clock, GitBranch } from 'lucide-react';
import { Agent, Model, OutputType } from '../../types';
import AgentToolIndicator from './AgentToolIndicator';
import Tooltip from '../common/Tooltip';

interface AgentTableProps {
  agents: Agent[];
  models: Model[];
  sortConfig: {
    field: 'name' | 'created_at' | 'updated_at';
    direction: 'asc' | 'desc';
  };
  agentWorkflows?: Record<number, { id: number; name: string }[]>;
  onSort: (field: 'name' | 'created_at' | 'updated_at') => void;
  onView: (agent: Agent) => void;
  onEdit: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
}

const AgentTable: React.FC<AgentTableProps> = ({
  agents,
  models,
  sortConfig,
  agentWorkflows = {},
  onSort,
  onView,
  onEdit,
  onDelete
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRowExpansion = (agentId: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
  };

  const getOutputTypeColor = (type: OutputType) => {
    const colors = {
      str: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600',
      model: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700'
    };
    return colors[type] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600';
  };

  const getToolIcon = (toolType: string) => {
    switch (toolType) {
      case 'web_search':
        return <Globe className="w-4 h-4" />;
      case 'function':
        return <Code className="w-4 h-4" />;
      case 'agent':
        return <Bot className="w-4 h-4" />;
      case 'get_current_time':
        return <Clock className="w-4 h-4" />;
      default:
        return <Wrench className="w-4 h-4" />;
    }
  };

  const getToolTypeLabel = (toolType: string) => {
    switch (toolType) {
      case 'web_search':
        return 'Web Search';
      case 'function':
        return 'Function';
      case 'agent':
        return 'Agent';
      case 'get_current_time':
        return 'Get Current Time';
      default:
        return toolType;
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <div className="bg-white dark:bg-cap-dark shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Enhanced Desktop Table View */}
      <div className="hidden xl:block">
        <div className="table-responsive">
          <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="w-12 px-3 py-3"></th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => onSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    {sortConfig.field === 'name' && (
                      <ChevronDown className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="w-16 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Output
                </th>
                <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Models
                </th>
                <th className="w-16 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Tools
                </th>
                <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Flows
                </th>
                <th
                  className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => onSort('updated_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Updated</span>
                    {sortConfig.field === 'updated_at' && (
                      <ChevronDown className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
              {agents.map((agent) => {
                const attachedModel = agent.output_model_id
                  ? models.find(m => m.model_id === agent.output_model_id) || agent.output_model
                  : null;
                const isExpanded = expandedRows.has(agent.agent_id);

                return (
                  <React.Fragment key={agent.agent_id}>
                    <tr className="hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                      <td className="px-3 py-4">
                        <button
                          onClick={() => toggleRowExpansion(agent.agent_id)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <Bot className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <Tooltip content={agent.name}>
                              <div className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
                                {agent.name}
                              </div>
                            </Tooltip>
                            {agent.instructions && (
                              <Tooltip content={agent.instructions}>
                                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {truncateText(agent.instructions, 80)}
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getOutputTypeColor(agent.output_type || 'str')}`}>
                          {agent.output_type || 'str'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {attachedModel ? (
                          <Tooltip content={attachedModel.class_name}>
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span className="text-sm text-gray-900 dark:text-warm-white truncate">
                                {truncateText(attachedModel.class_name, 25)}
                              </span>
                            </div>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {agent.tools && agent.tools.length > 0 ? (
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1">
                              {agent.tools.slice(0, 2).map((tool, index) => (
                                <Tooltip key={index} content={`${tool.name} (${getToolTypeLabel(tool.type)})`}>
                                  <div className="text-gray-600 dark:text-gray-400">
                                    {getToolIcon(tool.type)}
                                  </div>
                                </Tooltip>
                              ))}
                              {agent.tools.length > 2 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">+{agent.tools.length - 2}</span>
                              )}
                            </div>
                            <AgentToolIndicator tools={agent.tools} />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {agentWorkflows[agent.agent_id] ? (
                          <Tooltip content={agentWorkflows[agent.agent_id].map(w => w.name).join(', ')}>
                            <div className="flex items-center space-x-1">
                              <GitBranch className="w-4 h-4 text-primary-500 flex-shrink-0" />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {agentWorkflows[agent.agent_id].length}
                              </span>
                            </div>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(agent.updated_at || agent.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-1">
                          <Tooltip content="View details">
                            <button
                              onClick={() => onView(agent)}
                              className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Edit agent">
                            <button
                              onClick={() => onEdit(agent)}
                              className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Delete agent">
                            <button
                              onClick={() => onDelete(agent)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50 dark:bg-gray-900">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="space-y-3">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white mb-2">Full Instructions</h4>
                              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {agent.instructions || 'No instructions provided'}
                              </p>
                            </div>
                            {agent.tools && agent.tools.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white mb-2">Tools ({agent.tools.length})</h4>
                                <div className="space-y-2">
                                  {agent.tools.map((tool, index) => (
                                    <div key={index} className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-warm-white">{tool.name}</div>
                                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded flex items-center space-x-1">
                                          {getToolIcon(tool.type)}
                                          <span>{getToolTypeLabel(tool.type)}</span>
                                        </span>
                                      </div>
                                      {tool.description && (
                                        <div className="text-sm text-gray-600 dark:text-gray-400">{tool.description}</div>
                                      )}
                                      {tool.user_location && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                          Location: {tool.user_location.city}, {tool.user_location.region}, {tool.user_location.country}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-900 dark:text-warm-white">Model:</span>
                                <span className="ml-2 text-gray-700 dark:text-gray-300">{agent.model || 'Not specified'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 dark:text-warm-white">Created:</span>
                                <span className="ml-2 text-gray-700 dark:text-gray-300">{formatDate(agent.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Medium Screen Table View */}
      <div className="hidden lg:block xl:hidden">
        <div className="table-responsive">
          <table className="w-full min-w-[700px] divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="w-12 px-3 py-3"></th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => onSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    {sortConfig.field === 'name' && (
                      <ChevronDown className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="w-16 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Output
                </th>
                <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Models
                </th>
                <th className="w-16 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Tools
                </th>
                <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Flows
                </th>
                <th
                  className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => onSort('updated_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Updated</span>
                    {sortConfig.field === 'updated_at' && (
                      <ChevronDown className={`w-4 h-4 ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="w-24 px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
              {agents.map((agent) => {
                const attachedModel = agent.output_model_id 
                  ? models.find(m => m.model_id === agent.output_model_id) || agent.output_model
                  : null;
                const isExpanded = expandedRows.has(agent.agent_id);

                return (
                  <React.Fragment key={agent.agent_id}>
                    <tr className="hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                      <td className="px-3 py-4">
                        <button
                          onClick={() => toggleRowExpansion(agent.agent_id)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-3">
                          <Bot className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <Tooltip content={agent.name}>
                              <div className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
                                {agent.name}
                              </div>
                            </Tooltip>
                            {agent.instructions && (
                              <Tooltip content={agent.instructions}>
                                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {truncateText(agent.instructions, 60)}
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getOutputTypeColor(agent.output_type || 'str')}`}>
                          {agent.output_type || 'str'}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        {attachedModel ? (
                          <Tooltip content={attachedModel.class_name}>
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span className="text-sm text-gray-900 dark:text-warm-white truncate">
                                {truncateText(attachedModel.class_name, 20)}
                              </span>
                            </div>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {agent.tools && agent.tools.length > 0 ? (
                          <div className="flex items-center space-x-1">
                            <div className="flex items-center space-x-1">
                              {agent.tools.slice(0, 1).map((tool, index) => (
                                <Tooltip key={index} content={`${tool.name} (${getToolTypeLabel(tool.type)})`}>
                                  <div className="text-gray-600 dark:text-gray-400">
                                    {getToolIcon(tool.type)}
                                  </div>
                                </Tooltip>
                              ))}
                              {agent.tools.length > 1 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">+{agent.tools.length - 1}</span>
                              )}
                            </div>
                            <AgentToolIndicator tools={agent.tools} />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        {agentWorkflows[agent.agent_id] ? (
                          <Tooltip content={agentWorkflows[agent.agent_id].map(w => w.name).join(', ')}>
                            <div className="flex items-center space-x-1">
                              <GitBranch className="w-4 h-4 text-primary-500 flex-shrink-0" />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {agentWorkflows[agent.agent_id].length}
                              </span>
                            </div>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-3 py-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(agent.updated_at || agent.created_at)}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex items-center space-x-1">
                          <Tooltip content="View details">
                            <button
                              onClick={() => onView(agent)}
                              className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Edit agent">
                            <button
                              onClick={() => onEdit(agent)}
                              className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Delete agent">
                            <button
                              onClick={() => onDelete(agent)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50 dark:bg-gray-900">
                        <td colSpan={8} className="px-4 py-4">
                          <div className="space-y-3">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white mb-2">Full Instructions</h4>
                              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {agent.instructions || 'No instructions provided'}
                              </p>
                            </div>
                            {agent.tools && agent.tools.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 dark:text-warm-white mb-2">Tools ({agent.tools.length})</h4>
                                <div className="space-y-2">
                                  {agent.tools.map((tool, index) => (
                                    <div key={index} className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                                      <div className="flex items-center justify-between mb-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-warm-white">{tool.name}</div>
                                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded flex items-center space-x-1">
                                          {getToolIcon(tool.type)}
                                          <span>{getToolTypeLabel(tool.type)}</span>
                                        </span>
                                      </div>
                                      {tool.description && (
                                        <div className="text-sm text-gray-600 dark:text-gray-400">{tool.description}</div>
                                      )}
                                      {tool.user_location && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                          Location: {tool.user_location.city}, {tool.user_location.region}, {tool.user_location.country}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-900 dark:text-warm-white">Model:</span>
                                <span className="ml-2 text-gray-700 dark:text-gray-300">{agent.model || 'Not specified'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 dark:text-warm-white">Created:</span>
                                <span className="ml-2 text-gray-700 dark:text-gray-300">{formatDate(agent.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Enhanced Mobile Card View */}
      <div className="lg:hidden">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {agents.map((agent) => {
            const attachedModel = agent.output_model_id 
              ? models.find(m => m.model_id === agent.output_model_id) || agent.output_model
              : null;

            return (
              <div key={agent.agent_id} className="p-4 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <Bot className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Tooltip content={agent.name} position="bottom">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
                            {agent.name}
                          </h3>
                        </Tooltip>
                      </div>
                      {agent.instructions && (
                        <Tooltip content={agent.instructions} position="bottom">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 text-truncate-2">
                            {agent.instructions}
                          </p>
                        </Tooltip>
                      )}
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getOutputTypeColor(agent.output_type || 'str')}`}>
                          {agent.output_type || 'str'}
                        </span>
                        <span>Updated: {formatDate(agent.updated_at || agent.created_at)}</span>
                      </div>
                      {attachedModel && (
                        <Tooltip content={attachedModel.class_name} position="bottom">
                          <div className="flex items-center space-x-1 mt-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {attachedModel.class_name}
                            </span>
                          </div>
                        </Tooltip>
                      )}
                      {agent.tools && agent.tools.length > 0 && (
                        <div className="flex items-center space-x-1 mt-1">
                          <div className="flex items-center space-x-1">
                            {agent.tools.slice(0, 3).map((tool, index) => (
                              <Tooltip key={index} content={`${tool.name} (${getToolTypeLabel(tool.type)})`} position="bottom">
                                <div className="text-gray-600 dark:text-gray-400">
                                  {getToolIcon(tool.type)}
                                </div>
                              </Tooltip>
                            ))}
                            {agent.tools.length > 3 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">+{agent.tools.length - 3}</span>
                            )}
                          </div>
                          <AgentToolIndicator tools={agent.tools} />
                        </div>
                      )}
                      {agentWorkflows[agent.agent_id] && (
                        <Tooltip content={agentWorkflows[agent.agent_id].map(w => w.name).join(', ')} position="bottom">
                          <div className="flex items-center space-x-1 mt-1">
                            <GitBranch className="w-3 h-3 text-primary-500" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {agentWorkflows[agent.agent_id].length} workflow{agentWorkflows[agent.agent_id].length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 ml-2">
                    <Tooltip content="View details">
                      <button
                        onClick={() => onView(agent)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Edit agent">
                      <button
                        onClick={() => onEdit(agent)}
                        className="p-2 text-gray-400 hover:text-yellow-600 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Delete agent">
                      <button
                        onClick={() => onDelete(agent)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AgentTable;